import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, ProColors } from '@treatwell/treatwell-aura';
import { css } from '@emotion/react';
import {
  getCountryCodeForRegionCode,
  parsePhoneNumber,
} from 'awesome-phonenumber';
import { FormContextData, useFormContext } from '../../state';
import { getCountryCodeFromTimezone } from '../../helpers';
import { useValidation } from './helpers/use-validation';

const countryCodes = [
  'AT',
  'BE',
  'BG',
  'CH',
  'CY',
  'CZ',
  'DE',
  'DK',
  'EE',
  'ES',
  'FI',
  'FR',
  'GB',
  'GR',
  'HR',
  'HU',
  'IE',
  'IT',
  'LT',
  'LU',
  'LV',
  'MT',
  'NL',
  'NO',
  'PL',
  'PT',
  'RO',
  'SE',
  'SI',
  'SK',
];

const phoneCodes = countryCodes.map(getCountryCodeForRegionCode);

function getDefaultCountryCode() {
  const tzCountry = getCountryCodeFromTimezone();
  return getCountryCodeForRegionCode(tzCountry);
}

function useDefaultCountryCode() {
  return useMemo(() => getDefaultCountryCode(), []);
}

type SelectPhoneCodeProps = {
  onSelectPhoneCode: (indicator: number) => void;
  phoneCode: number;
};

function SelectPhoneCode({
  onSelectPhoneCode,
  phoneCode,
}: SelectPhoneCodeProps) {
  return (
    <div
      css={css({
        paddingRight: 8,
        borderRight: `1px solid ${ProColors.navy[300]}`,
        display: 'flex',
        alignItems: 'center',
      })}
    >
      <select
        id="phone-number-country-code"
        className="aura--copy-normal"
        role="listbox"
        css={css({
          // Disable browser style and re-add custom down chevron
          appearance: 'none',
          background: `url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 8 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M6.09763 2.76431L6.56904 3.23572L4 5.80475L1.43097 3.23572L1.90237 2.76431L4 4.86194L6.09763 2.76431Z' fill='${encodeURIComponent(
            ProColors.navy[500],
          )}'/%3E%3C/svg%3E")`,
          backgroundPosition: 'center right',
          backgroundRepeat: 'no-repeat',
          width: 45,
          border: 0,
          padding: 0,
          backgroundColor: 'transparent',
          color: ProColors.navy[500],
          outline: 'none',
        })}
        value={phoneCode}
        onChange={event => {
          onSelectPhoneCode(parseInt(event.target.value, 10));
        }}
      >
        {phoneCodes
          .sort((a, b) => a.toString().localeCompare(b.toString()))
          .map(i => (
            <option key={i} value={i} role="option">
              {i}
            </option>
          ))}
      </select>
    </div>
  );
}

// TODO preselect correct phone code when localStorage is filled
export function PhoneNumber(): JSX.Element {
  const { t } = useTranslation('input', { keyPrefix: 'phoneNumber' });
  const {
    data: { phoneNumber },
    setData,
  } = useFormContext();

  const defaultCountryCode = useDefaultCountryCode();
  // avoid refreshing initialPhoneNumber, it is only used once
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialPhoneNumber = useMemo(() => parsePhoneNumber(phoneNumber), []);

  const [countryCode, setCountryCode] = useState(
    initialPhoneNumber.countryCode || defaultCountryCode,
  );
  const [number, setNumber] = useState(
    initialPhoneNumber.number?.significant || '',
  );

  useEffect(() => {
    const parsed = parsePhoneNumber(`+${countryCode}${number}`);
    setData(current => ({
      ...current,
      phoneNumber: parsed.number?.e164 || '',
    }));
  }, [countryCode, number, setData]);

  const hasValue = useCallback(
    (data: FormContextData) =>
      data.phoneNumber ? null : 'PhoneNumber is empty',
    [],
  );
  const validate = useCallback((data: FormContextData) => {
    const res = parsePhoneNumber(data.phoneNumber);
    return res.valid ? null : 'PhoneNumber is invalid';
  }, []);
  const extraInputProps = useValidation(t, hasValue, validate);

  return (
    <Input
      id="phone-number"
      type="tel"
      label={t('label')}
      value={number}
      onValueChange={setNumber}
      autoComplete="tel"
      innerLeftComponent={
        <SelectPhoneCode
          onSelectPhoneCode={setCountryCode}
          phoneCode={countryCode}
        />
      }
      {...extraInputProps}
    />
  );
}
